import { useCallback, useEffect, useState } from 'react';
import {
  assinarFila,
  listarFila,
  listarRejeitados,
  reenviarFila,
  type ItemDaFila,
} from '@/lib/offline';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Estado de conexão e da fila de escritas pendentes.
 *
 * `navigator.onLine` só sabe que existe uma interface de rede ativa — diz
 * "online" num wi-fi sem saída para a internet. Por isso o reenvio não confia
 * nele para decidir sucesso: ele dispara a tentativa, e é a resposta do
 * servidor que decide. O indicador usa `onLine` porque para avisar o usuário
 * ele é bom o bastante, e falso positivo ali só custa uma tentativa.
 */
export function useOffline() {
  const token = useAuthStore((s) => s.token) || '';
  const [online, setOnline] = useState(() => navigator.onLine);
  const [fila, setFila] = useState<ItemDaFila[]>([]);
  const [rejeitados, setRejeitados] = useState<ItemDaFila[]>([]);
  const [sincronizando, setSincronizando] = useState(false);

  const recarregar = useCallback(async () => {
    setFila(await listarFila());
    setRejeitados(listarRejeitados());
  }, []);

  const sincronizar = useCallback(async () => {
    if (!token || sincronizando) return;
    const pendentes = await listarFila();
    if (pendentes.length === 0) return;

    setSincronizando(true);
    try {
      const r = await reenviarFila(API_URL, token);
      if (r.enviados > 0) toast(`${r.enviados} registro(s) sincronizado(s).`);
      if (r.falharam > 0) {
        toast(`${r.falharam} registro(s) recusado(s) pelo servidor. Veja os detalhes no aviso.`);
      }
    } finally {
      setSincronizando(false);
      await recarregar();
    }
  }, [token, sincronizando, recarregar]);

  useEffect(() => {
    void recarregar();
    return assinarFila(() => void recarregar());
  }, [recarregar]);

  useEffect(() => {
    const aoVoltar = () => {
      setOnline(true);
      void sincronizar();
    };
    const aoCair = () => setOnline(false);

    window.addEventListener('online', aoVoltar);
    window.addEventListener('offline', aoCair);
    return () => {
      window.removeEventListener('online', aoVoltar);
      window.removeEventListener('offline', aoCair);
    };
  }, [sincronizar]);

  // Tentativa de sincronizar ao abrir a aplicação: o evento `online` não
  // dispara para quem já estava conectado ao carregar a página, e sem isto uma
  // fila deixada de ontem só sairia depois de a conexão cair e voltar.
  useEffect(() => {
    if (online && token) void sincronizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { online, fila, rejeitados, sincronizando, sincronizar, recarregar };
}
